import React, { ComponentProps, memo, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { ErrorBoundary } from '@sentry/react';
import { GetSoftphoneSettingsResponse } from '@weave/schema-gen-ts/dist/schemas/phone/softphones/softphones.pb';
import { QueryObserverResult, useQueryClient } from 'react-query';
import { createContext, useContextSelector } from 'use-context-selector';
import { DataSourcesHooks } from '@frontend/api-data-sources';
import { DevicesApi, DevicesQueries } from '@frontend/api-devices';
import { E911Api, E911QueryKeys } from '@frontend/api-e911-addresses';
import { PersonAPI } from '@frontend/api-person';
import SchemaSipProfileService from '@frontend/api-sip-profile';
import { SoftphoneQueries, SoftphoneTypes } from '@frontend/api-softphone';
import { useAppSettingsShallowStore } from '@frontend/app-settings-store';
import { getUser, getWeaveToken } from '@frontend/auth-helpers';
import appConfig from '@frontend/env';
import { http } from '@frontend/fetch';
import { OutboundSelectedPhoneNumberProps, useOutboundCallerIdNumbers } from '@frontend/generic-dialpad-accessories';
import { useHasPhoneSystemAccess, usePhoneConfigStore } from '@frontend/phone-config';
import { usePhoneSyncMutations } from '@frontend/phone-sync';
import { useConditionalChannelSubscription } from '@frontend/queue';
import { useMutation, useQuery } from '@frontend/react-query-helpers';
import { useAppScopeStore } from '@frontend/scope';
import { SoftphoneStateInterface } from '@frontend/softphone-state';
import {
  MetricEventsRequest,
  SoftphoneAudioProvider,
  SoftphoneCallStateProvider,
  SoftphoneClientProvider,
  SoftphoneDialerProvider,
  SoftphoneDirectoryProvider,
  SoftphoneE911Provider,
  SoftphoneEventsProvider,
  SoftphoneHistoryProvider,
  SoftphoneMediaDevicesProvider,
  SoftphoneMetricsProvider,
  SoftphoneParkSlotsProvider,
  SoftphoneRouterProvider,
  SoftphoneSettingsProvider,
  SoftphoneSubscriptionsProvider,
  SoftphoneToastProvider,
  SoftphoneUsersProvider,
  SoftphoneWidgetControlProvider,
  useSoftphoneDialer,
  useSoftphoneSettings,
  useSoftphoneWidgetControl,
} from '@frontend/softphone2';
import { TAB_ID } from '@frontend/tab-management';
import { useModalControl } from '@frontend/design-system';
import { useSoftphoneRegistrationQuery, useSoftphoneSettingsQuery } from './query-hooks';
import { SoftphoneCallPopManager } from './softphone-call-pop-manager';
import { SoftphoneDisconnectModal } from './softphone-disconnect-modal';

export const getPersonApis = ({
  dataSourceIds,
}: {
  dataSourceIds?: string[];
}): ComponentProps<typeof SoftphoneDirectoryProvider>['personApi'] => ({
  getPersonsByName: (name: string) => PersonAPI.getPersonsByName(name).then((res) => res.rows),
  getPersons: (skip, limit) =>
    PersonAPI.getPersonsV2({ order: ['last_name', 'first_name'], skip, limit, status: 'Active', dataSourceIds }).then(
      (res) => res.rows
    ),
  getPersonByPhone: PersonAPI.getPersonByPhone,
  getPersonsByPhone: (phone: string) =>
    PersonAPI.getPersonsV2({
      order: ['last_name', 'first_name'],
      skip: 0,
      limit: 25,
      status: 'Active',
      query: phone,
      dataSourceIds,
    }).then((res) => res.rows),
  getPersonById: PersonAPI.getPersonExtended,
  getPersonImage: PersonAPI.getPersonImage,
});

const defaultUsers: any[] = [];
const defaultParkSlots: any[] = [];
const defaultAddresses: any[] = [];

type SoftphoneInnerManagerContextValue = {
  error: Error | undefined;
  isLoading: boolean;
  isConnected: boolean;
  sipProfile: SoftphoneTypes.SipProfile | undefined;
  isCallWaitingIndicatorBeepEnabled: boolean;
};
const SoftphoneInnerManagerContext = createContext({} as SoftphoneInnerManagerContextValue);

type SoftphoneManagerContextValue = {
  isActiveTab: boolean;
  activeTab: string;
  hasActiveCalls: boolean;
  isSoftphoneDataFetching: boolean;
  allowSoftphoneToRegister: boolean;
};
const SoftphoneManagerContext = createContext({} as SoftphoneManagerContextValue);

const mobileBreakpoint = 850;

type SoftphoneManagerInnerProps = SoftphoneManagerProps & {
  settings: SoftphoneTypes.SoftphoneSettings;
  softphone: SoftphoneTypes.SoftphoneData;
  sipProfile: SoftphoneTypes.SipProfile;
  unregisterTab: () => void;
  hasActiveCalls: boolean;
  setHasActiveCalls: (hasActiveCalls: boolean) => void;
  userAgent: string;
  refetchSettings: () => Promise<QueryObserverResult<unknown, unknown>>;
  postEvent: (softphoneEvent: MetricEventsRequest) => Promise<any> | void;
};

type SoftphoneManagerProps = {
  children: React.ReactNode;
  onCallIsActive?: () => void;
  onAllCallsEnded?: () => void;
};

export const SoftphoneManager = memo(({ onCallIsActive, onAllCallsEnded, children }: SoftphoneManagerProps) => {
  const {
    hasActiveSoftphoneCalls,
    setHasActiveSoftphoneCalls,
    lastRegisteredCallId,
    overrideSoftphoneCheck,
    activeTab,
  } = useAppSettingsShallowStore(
    'hasActiveSoftphoneCalls',
    'setHasActiveSoftphoneCalls',
    'lastRegisteredCallId',
    'overrideSoftphoneCheck',
    'activeTab'
  );
  const phoneConfig = usePhoneConfigStore((store) => store.phoneConfig);
  const user = getUser();
  const version = appConfig.VERSION;
  const userAgent = `Weave2.0/${version ?? 'unknown'}`;

  const { isRegistrationAllowed, isRegistrationStatusFetching, firstTimeRegistering, registration } =
    useSoftphoneRegistrationQuery({
      tenantId: phoneConfig?.tenantId ?? '',
      sipProfileId: phoneConfig?.sipProfileId ?? '',
      enabled: !!user?.userID && !!phoneConfig?.sipProfileId && phoneConfig.deviceType === 'DEVICE_TYPE_SOFTPHONE',
      lastRegisteredCallId,
    });

  const {
    settings,
    softphone,
    sipProfile,
    refetchSettings: _refetchSettings,
    isFetching: isSettingsFetching,
  } = useSoftphoneSettingsQuery({
    userAgent,
    tenantId: phoneConfig?.tenantId ?? '',
    sipProfileId: phoneConfig?.sipProfileId ?? '',
    enabled: !!user?.userID,
  });

  const refetchSettings = () => {
    return _refetchSettings();
  };

  const { unregisterTab } = SoftphoneStateInterface;
  const isActiveTab = activeTab === TAB_ID;

  const allowSoftphoneToRegister = overrideSoftphoneCheck || (isActiveTab && isRegistrationAllowed);

  const isSoftphoneDataFetching = isSettingsFetching || isRegistrationStatusFetching;

  const postEvent = useCallback(
    (event: MetricEventsRequest) => {
      if (import.meta.env.MODE === 'development') {
        console.log('SoftphoneMetricsProvider POST', event);
        return;
      }

      if (!softphone?.metricsUrl) {
        return;
      }
      return http.post<unknown>(softphone.metricsUrl, {
        ...event,
        userAgent,
      });
    },
    [softphone?.metricsUrl, userAgent]
  );

  const managerContextValue = useMemo(
    () => ({
      isActiveTab,
      activeTab,
      hasActiveCalls: hasActiveSoftphoneCalls,
      isSoftphoneDataFetching,
      allowSoftphoneToRegister,
    }),
    [isActiveTab, activeTab, hasActiveSoftphoneCalls, isSoftphoneDataFetching, allowSoftphoneToRegister]
  );

  useEffect(() => {
    if (registration && isActiveTab) {
      postEvent({
        event: 'softphone_connection_check',
        registrationState: registration.active ? 'active' : firstTimeRegistering ? 'none' : 'expired',
        callIDComparison: lastRegisteredCallId
          ? lastRegisteredCallId === registration.callId
            ? 'match'
            : 'mismatch'
          : 'none',
        connectionDecision: isRegistrationAllowed ? 'allow' : 'block',
      });
    }
  }, [firstTimeRegistering, isActiveTab, isRegistrationAllowed, lastRegisteredCallId, postEvent, registration]);

  if (settings && softphone && sipProfile && allowSoftphoneToRegister) {
    return (
      <SoftphoneManagerContext.Provider value={managerContextValue}>
        <SoftphoneManagerInner
          onAllCallsEnded={onAllCallsEnded}
          onCallIsActive={onCallIsActive}
          settings={settings}
          softphone={softphone}
          sipProfile={sipProfile}
          refetchSettings={refetchSettings}
          unregisterTab={() => unregisterTab(TAB_ID)}
          hasActiveCalls={hasActiveSoftphoneCalls}
          setHasActiveCalls={setHasActiveSoftphoneCalls}
          userAgent={userAgent}
          postEvent={postEvent}
        >
          {children}
        </SoftphoneManagerInner>
      </SoftphoneManagerContext.Provider>
    );
  }
  return <SoftphoneManagerContext.Provider value={managerContextValue}>{children}</SoftphoneManagerContext.Provider>;
});

SoftphoneManager.displayName = 'SoftphoneManager';

export const SoftphoneManagerInner = memo(
  ({
    onCallIsActive,
    onAllCallsEnded,
    settings,
    softphone,
    sipProfile,
    children,
    hasActiveCalls,
    setHasActiveCalls,
    userAgent,
    refetchSettings,
    postEvent,
  }: SoftphoneManagerInnerProps) => {
    const [state, setState] = useState<'off' | 'on'>('on');

    const [isRefreshing, setIsRefreshing] = useState(false);
    const token = getWeaveToken();
    const user = getUser();
    const [currE911AddressId, setCurrE911AddressId] = useState<string>();
    const [isConnected, setIsConnected] = useState(false);
    const [error, setError] = useState<Error>();
    const phoneConfigTenantId = usePhoneConfigStore((store) => store.phoneConfig?.tenantId);
    const phoneConfigUserId = usePhoneConfigStore((store) => store.phoneConfig?.activeUserId);
    const disconnectDeviceModalProps = useModalControl();
    const { demoSourceIds } = DataSourcesHooks.useDemoLocationSourceIdsShallowStore('demoSourceIds');
    const { selectedLocationIds } = useAppScopeStore();
    const hasPhoneSystemAccess = useHasPhoneSystemAccess({ locationIds: selectedLocationIds });
    const isCallWaitingIndicatorBeepEnabled = softphone.callWaitingIndicatorBeep;

    const { disconnectDevice } = usePhoneSyncMutations();
    const queryClient = useQueryClient();
    const queryKey = [phoneConfigTenantId, ...SoftphoneQueries.queryKeys.getCurrentUserSoftphone];

    /**
     * TODO: this setter is in preparation of allowing users to select their outbound id.
     * It isn't supporte yet in the backend, so once it is, we will enable the UI for it.
     */
    const { numbers, selectedPhoneNumber, setSelectedPhoneNumber } = useOutboundCallerIdNumbers({
      id: sipProfile.id,
    });

    const { users, otherExtensions } = useMemo(() => {
      return {
        users: softphone.extensions.filter((user) => !!user.presenceUri),
        otherExtensions: softphone.extensions.filter((user) => !user.presenceUri),
      };
    }, [softphone.extensions]);

    const { data: { addresses } = { addresses: [] }, isRefetching: isLoadingAddresses } = useQuery({
      queryKey: [phoneConfigTenantId, ...E911QueryKeys.queryKeys.listAddresses()],
      queryFn: () => E911Api.list({ tenantId: phoneConfigTenantId ?? '' }),
      enabled: !!user?.userID,
      keepPreviousData: true,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    });

    const isLoading = isLoadingAddresses || isRefreshing;

    /**
     * When softphone data changes, trigger setup_autoprovision_finish event
     */
    useEffect(() => {
      if (softphone) {
        postEvent({ event: 'setup_autoprovision_finish' });
      }
    }, [softphone, postEvent]);

    useEffect(() => {
      if (sipProfile.e911AddressId) {
        setCurrE911AddressId(sipProfile.e911AddressId);
      }
    }, [sipProfile.e911AddressId]);

    const onSelectAddress = useCallback(
      (addressId: string) => {
        if (!sipProfile.id) {
          console.warn('E911 address chosen, but no sip profile id provided');
          return;
        }
        const prev = currE911AddressId;
        setCurrE911AddressId(addressId);
        return DevicesApi.UpdateDeviceE911Address({
          deviceId: softphone.id ?? '',
          e911Address: {
            id: addressId,
            name: '',
          },
        })
          .then((_res) => {
            // refetch();
          })
          .catch(() => {
            setCurrE911AddressId(prev);
          });
      },
      [currE911AddressId, sipProfile.id, softphone.id]
    );

    const onRestart: () => Promise<any> = useCallback(() => {
      setIsRefreshing(true);
      return refetchSettings().finally(() => {
        setIsRefreshing(false);
      });
    }, [refetchSettings]);

    const updateDNDMutation = useMutation({
      mutationFn: ({ enabled }: { enabled: boolean }) => {
        return SchemaSipProfileService.UpdateDND({ sipProfileId: sipProfile.id, doNotDisturb: enabled });
      },
      onMutate: ({ enabled }) => {
        queryClient.cancelQueries(queryKey);

        const oldData = queryClient.getQueryData<GetSoftphoneSettingsResponse>(queryKey);

        const newData = {
          ...oldData,
          softphones: oldData?.softphones.map((softphone) => {
            return {
              ...softphone,
              sipProfiles: softphone.sipProfiles.map((profile) =>
                profile.id === sipProfile.id ? { ...profile, doNotDisturb: enabled } : profile
              ),
            };
          }),
        };
        queryClient.setQueryData(queryKey, newData);
        return oldData;
      },
      onError: (error, _variables, context) => {
        console.error('Error updating DND:', error);
        if (context) queryClient.setQueryData<GetSoftphoneSettingsResponse>(queryKey, context);
      },
      onSuccess: () => {
        queryClient.invalidateQueries(queryKey);
      },
    });

    const hasAllTheData = !!(softphone && settings && sipProfile.id && user && token && !isLoading);
    const isConnectedToUserSoftphone = !!softphone && user?.userID === phoneConfigUserId;
    const isMounted = hasAllTheData && isConnectedToUserSoftphone && !!hasPhoneSystemAccess;
    const isReallyConnected = isMounted && isConnected;

    const onDisconnect = useCallback(() => {
      setSelectedPhoneNumber(undefined);
      setIsConnected(false);
      setState('off');
    }, [setSelectedPhoneNumber]);

    const onConnect = useCallback(() => {
      setIsConnected(true);
      setState('on');
    }, []);

    const value = useMemo(
      () => ({
        error,
        isLoading,
        isConnected: isReallyConnected,
        sipProfile,
        isCallWaitingIndicatorBeepEnabled,
      }),
      [error, isLoading, isReallyConnected, sipProfile, isCallWaitingIndicatorBeepEnabled]
    ) satisfies SoftphoneInnerManagerContextValue;

    const eventHandlers = useMemo<ComponentProps<typeof SoftphoneEventsProvider>['handlers']>(
      () => ({
        'call-state.changed': (state) => {
          if (state.establishedCalls?.length > 0) {
            setHasActiveCalls(true);
            SoftphoneStateInterface.broadcastActiveCall({ tabId: TAB_ID, hasActiveCall: true });
            return onCallIsActive?.();
          } else {
            setHasActiveCalls(false);
            SoftphoneStateInterface.broadcastActiveCall({ tabId: TAB_ID, hasActiveCall: false });
            return onAllCallsEnded?.();
          }
        },
      }),
      [onCallIsActive, onAllCallsEnded, setHasActiveCalls]
    );

    const memoizedPersonApi = useMemo(() => getPersonApis({ dataSourceIds: demoSourceIds }), [demoSourceIds]);

    const disconnectModalProps = useMemo(
      () => ({
        disconnectDevice,
        ...disconnectDeviceModalProps,
      }),
      [disconnectDevice, disconnectDeviceModalProps]
    );

    const { data: { device } = { device: undefined } } = DevicesQueries.useGetDeviceQuery({
      deviceId: softphone.id,
    });

    if (state !== 'off') {
      return (
        <SoftphoneInnerManagerContext.Provider value={value} key={sipProfile.username}>
          <ErrorBoundary
            fallback={({ error }) => (
              <SoftphoneProvidersErrorBoundary
                onError={() => {
                  error instanceof Error ? setError(error as Error) : setError(new Error('unknown error occurred!'));
                }}
              >
                {children}
              </SoftphoneProvidersErrorBoundary>
            )}
          >
            <SoftphoneMetricsProvider postEvent={postEvent}>
              <SoftphoneToastProvider defaultTimeout={2000}>
                <SoftphoneEventsProvider handlers={eventHandlers}>
                  <SoftphoneClientProvider
                    setError={setError}
                    onConnect={onConnect}
                    onDisconnect={onDisconnect}
                    onRestart={onRestart}
                    deviceName={sipProfile.name}
                    extensionNumber={sipProfile.extensionNumber}
                    proxy={settings.proxy}
                    username={sipProfile.username}
                    domain={sipProfile.domain}
                    password={sipProfile.password}
                    userAgent={userAgent}
                    extensions={softphone.extensions}
                  >
                    <SoftphoneSettingsProvider
                      disconnectDevice={disconnectDevice}
                      openDisconnectModal={() => disconnectDeviceModalProps.openModal()}
                      selectedOutboundPhoneNumber={selectedPhoneNumber}
                      setCurrentPhoneNumber={setSelectedPhoneNumber}
                      availablePhoneNumbers={numbers}
                      phoneConfigTenantId={phoneConfigTenantId}
                      isCallWaitingIndicatorBeepEnabled={isCallWaitingIndicatorBeepEnabled}
                      updateDnd={updateDNDMutation}
                      isDndEnabled={sipProfile.doNotDisturb}
                      device={device}
                    >
                      <SoftphoneDialerProvider>
                        <SoftphoneMediaDevicesProvider>
                          <SoftphoneAudioProvider>
                            <SoftphoneWidgetControlProvider mobileBreakpoint={mobileBreakpoint}>
                              <SoftphoneCallStateProvider>
                                <SoftphoneDirectoryProvider personApi={memoizedPersonApi}>
                                  <SoftphoneUsersProvider
                                    otherExtensions={otherExtensions}
                                    users={users ?? defaultUsers}
                                  >
                                    <SoftphoneParkSlotsProvider parkSlots={softphone.parkSlots ?? defaultParkSlots}>
                                      <SoftphoneSubscriptionsProvider>
                                        <SoftphoneE911Provider
                                          e911Addresses={addresses ?? defaultAddresses}
                                          currentE911AddressId={currE911AddressId}
                                          onSelectE911Address={onSelectAddress}
                                        >
                                          <SoftphoneHistoryProvider>
                                            <SoftphoneRouterProvider>
                                              <SoftphoneCallPopManager
                                                isCallWaitingIndicatorBeepEnabled={isCallWaitingIndicatorBeepEnabled}
                                                hasActiveCalls={hasActiveCalls}
                                                softphoneData={softphone}
                                              />
                                              <SoftphoneDisconnectModal {...disconnectModalProps} />
                                              <Wrapper numbers={numbers}>{children}</Wrapper>
                                            </SoftphoneRouterProvider>
                                          </SoftphoneHistoryProvider>
                                        </SoftphoneE911Provider>
                                      </SoftphoneSubscriptionsProvider>
                                    </SoftphoneParkSlotsProvider>
                                  </SoftphoneUsersProvider>
                                </SoftphoneDirectoryProvider>
                              </SoftphoneCallStateProvider>
                            </SoftphoneWidgetControlProvider>
                          </SoftphoneAudioProvider>
                        </SoftphoneMediaDevicesProvider>
                      </SoftphoneDialerProvider>
                    </SoftphoneSettingsProvider>
                  </SoftphoneClientProvider>
                </SoftphoneEventsProvider>
              </SoftphoneToastProvider>
            </SoftphoneMetricsProvider>
          </ErrorBoundary>
        </SoftphoneInnerManagerContext.Provider>
      );
    }
    return <SoftphoneInnerManagerContext.Provider value={value}>{children}</SoftphoneInnerManagerContext.Provider>;
  }
);

SoftphoneManagerInner.displayName = 'SoftphoneManagerInner';

export const useSoftphoneManager = <T extends any>(selector: (val: SoftphoneManagerContextValue) => T) => {
  return useContextSelector(SoftphoneManagerContext, selector);
};

export const useSoftphoneInnerManager = <T extends any>(selector: (val: SoftphoneInnerManagerContextValue) => T) => {
  return useContextSelector(SoftphoneInnerManagerContext, selector);
};

type Props = { children: ReactNode; onError: () => void };
const SoftphoneProvidersErrorBoundary = ({ children, onError }: Props) => {
  useEffect(() => {
    onError();
  }, [onError]);
  return <>{children}</>;
};

const Wrapper = ({ children, numbers }: { children: ReactNode; numbers: OutboundSelectedPhoneNumberProps[] }) => {
  const setSoftphoneDialer = useSoftphoneDialer((ctx) => ctx.setCurrentDial);
  const softphoneIsConnected = useSoftphoneInnerManager((ctx) => ctx.isConnected);

  const setSoftphoneOutboundNumber = useSoftphoneSettings((ctx) => ctx.setCurrentPhoneNumber);
  const currentSoftphoneOutboundNumber = useSoftphoneSettings((ctx) => ctx.selectedOutboundPhoneNumber);
  const openSoftphone = useSoftphoneWidgetControl((ctx) => ctx.open);

  const handler = useCallback(
    (event: { payload: { type: string; dialNumber: string; outboundNumber?: string } }) => {
      if (event.payload.type === 'place-call' && softphoneIsConnected) {
        const { dialNumber, outboundNumber: specificOutboundNumber } = event.payload;
        const outboundNumber = specificOutboundNumber
          ? numbers.find((item) => item.number === specificOutboundNumber)
          : currentSoftphoneOutboundNumber;
        setSoftphoneDialer(dialNumber);
        setSoftphoneOutboundNumber(outboundNumber);
        openSoftphone?.();

        return true;
      }

      return false;
    },
    [
      softphoneIsConnected,
      numbers,
      currentSoftphoneOutboundNumber,
      setSoftphoneDialer,
      setSoftphoneOutboundNumber,
      openSoftphone,
    ]
  );

  useConditionalChannelSubscription(
    'dialpad',
    useCallback(
      (event) => {
        if (event.payload.type === 'place-call') {
          handler(event);
          return true;
        }
        return false;
      },
      [handler]
    ),
    softphoneIsConnected // Only subscribe when connected
  );

  return <>{children}</>;
};
